<script setup>
  import { ref, onMounted, getCurrentInstance, inject } from 'vue'
  import { useRouter } from 'vue-router'
  import { storeToRefs } from 'pinia';
  import { useUserStore } from '../../../../stores/user';
  import { usePageLoadingStore } from "../../../../../../stores/pageLoading";
  import JobFairJobNoticeDetailModal from "@/pages/user/components/modal/jobFair/JobFairJobNoticeDetailModal.vue";
  import MyPageJobPostingApplicantListView from './MyPageJobPostingApplicantListView.vue';
  import mypageJobFairJobNoticeService from '../../../../services/mypage/jobFair/mypageJobFairJobNoticeService';
  import JobFairReusmeCreatePolicyModal from '../../../../components/modal/jobFair/JobFairReusmeCreatePolicyModal.vue';
  import mypageJobFairService from "../../../../services/mypage/jobFair/mypageJobFairService";

  const { user } = storeToRefs(useUserStore());

  const globals = getCurrentInstance().appContext.config.globalProperties;
  const { $yyyymmddHHmmss, $toast, $gbNameKR, $userGbKR, $number, $pageListBoardNo, alert, confirm } = globals;

  const router = useRouter()
  const loadingStore = usePageLoadingStore()

  const currentPage = ref(1);
  const pageSize = ref(10);
  const totalPages = ref(1);
  const totalCount = ref(0);
  const list = ref([]);

  const pageSizeGroup = ref([
    { label:"10개씩 보기", value: "10" },
    { label:"30개씩 보기", value: "30" },
    { label:"50개씩 보기", value: "50" },
    { label:"100개씩 보기", value: "100" },
  ])

  const selectedJobNoticeSeq = ref('');
  const selectedFileYn = ref('');
  const selectedCorpId = ref('');
  const selectedUserId = ref('');
  const jobFairReusmeCreatePolicyModal = ref(null);

  // 모달
  const jobNoticeModal = ref(null); // 채용공고
  const isApplicantView = ref(false); // 입사지원 View 여부

  const retrieveList = async () => {
    const paramObj = {
      corpId: user.value.userInfo.corporation?.corpId || '',
      pageNumber: currentPage.value,
      size: pageSize.value,
    };
    selectedCorpId.value = paramObj.corpId;
    selectedUserId.value = user.value.userInfo.userId;

    loadingStore.loading = true;
    mypageJobFairJobNoticeService.retrieveCorpJobNoticeListPaging(paramObj)
      .then( async (res) => {
        loadingStore.loading = false;
        if(res.data.code.toLowerCase() === 'success') {
          const result = res.data.result;
          totalPages.value = result.totalPages;
          totalCount.value = result.totalElements;
          list.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
        }
        loadingStore.loading = false;
      })
      .catch( (res) =>  {
        loadingStore.loading = false;
      });
  }

  const onSelectedPage = (pageNo) => {
      currentPage.value = pageNo;
      retrieveList();
  }

  // 페이지당 개수 변경
  const onSelectPageSize = (option) => {
    pageSize.value = option.value;
    currentPage.value = 1;
    retrieveList();
  }

  const onClickJobNotice = async (jobNotice) => {
    await jobNoticeModal.value.show({
      jobNoticeSeq: jobNotice.jobNoticeSeq,
      corpId: jobNotice.corpId,
    });
  };

  const onClickApplicantCount = ({ jobNoticeSeq, fileYn }) => {
    selectedJobNoticeSeq.value = jobNoticeSeq;
    selectedFileYn.value = fileYn;
    isApplicantView.value = true;
  };

  const onClickCreateCorpJobNotice = () => {
    const corpId = user.value.userInfo.corporation.corpId;

    // 기업 채용공고 등록 시 기업정보 등록 및 정보제공동의여부 확인
    mypageJobFairService.retrieveRecruitPrivacyAgreeYnByCorpId(corpId)
      .then( async (res) => {
        loadingStore.loading = false;
        if(res.data.code.toLowerCase() === 'success') {
          const result = res.data.result;

          if (result !== 'Y') {  // 기업정보 및 정보제공동의여부 내역 없을 때
            const ok = await alert(["등록된 기업정보가 없습니다.", "기업정보 등록 후 채용공고 등록이 가능합니다."]);
            if(ok) {
              router.push({ name: 'MyPageCorpInfoCreate' });
            }
          } else {
            router.push({ name: 'MyPageCorpJobNoticeCreate' });
          }
        }
      })
      .catch( (res) =>  {
        loadingStore.loading = false;
      });
  };

  const onClickEditJobNotice = (jobNoticeSeq) => {
    router.push({ name: 'MyPageCorpJobNotice', query : { jobNoticeSeq }});
  };

  const onClickDeleteJobNotice = async (jobNoticeSeq) => {
    const ok = await confirm(["채용공고를 삭제하시겠습니까?"], "삭제");
    if(ok) {
      mypageJobFairJobNoticeService.deleteJobNotice({ jobNoticeSeq })
        .then( async (res) => {
          loadingStore.loading = false;
          if(res.data.code.toLowerCase() === 'success') {
            alert("채용공고가 삭제되었습니다.");
            retrieveList();
          } else {
            if (res.response.data.result.message) {
              alert([res.response.data.result.message]);
            } else {
              alert("채용공고 삭제를 실패하였습니다.");
            }
          }
        })
        .catch( (res) =>  {
          alert([res.response.data.result.message]);
          loadingStore.loading = false;
        });
    }
  };

  onMounted(() => {
    retrieveList();
  });
</script>

<template>
  <div v-if="!isApplicantView">

    <div class="list-top mb-n-xs">
      <KSAPrimaryButton text="채용공고 등록" @click="onClickCreateCorpJobNotice()" />
    </div>
    <!-- 검색 자리 -->
    <div class="list-top">
        <p>총 <span>{{ $number(totalCount) }}</span>개</p>
        <KSASelect :options="pageSizeGroup" class="select" @selectOption="onSelectPageSize"/>
    </div>
    <div class="table-form board-mobile-vertical">
        <table>
            <tr>
                <th>NO</th>
                <th>채용상태</th>
                <th>채용공고 제목</th>
                <th>입사지원</th>
                <th>채용기간</th>
                <th>최근 수정일</th>
                <th>등록일</th>
                <th>관리</th>
            </tr>
            <tr v-for="jobNotice in list"
                :key="jobNotice.jobNoticeSeq"
                :style="{ color: jobNotice.jobNoticeStateGb === '03' ? '#86889A' : 'inherit'}"
            >
              <td>{{ jobNotice.boardNo }}</td>
              <td>{{ $gbNameKR('JOB_NOTICE_STATE_GB', jobNotice.jobNoticeStateGb) }}</td><!-- 01 신청 02 취소 03 승인 04 거절 -->
              <td @click="onClickJobNotice(jobNotice)">
                <p class="clickable blue text-overflow long-overflow">{{ jobNotice.jobName }}</p>
              </td>
              <td @click="onClickApplicantCount(jobNotice)">
                <span class="clickable">{{ jobNotice.applicantCnt }}</span>
              </td>
              <td>{{ $yyyymmdd(jobNotice.recruitStartYmd) }} ~ {{ $yyyymmdd(jobNotice.recruitEndYmd) }}</td>
              <td>{{ $yyyymmddHHmmss(jobNotice.modifyDate) }}</td>
              <td>{{ $yyyymmddHHmmss(jobNotice.createDate) }}</td>
              <td>
                <KSATextButton text="수정" @click="onClickEditJobNotice(jobNotice.jobNoticeSeq)" class="clickable"/>&nbsp;
                <KSATextButton v-if="jobNotice.applicantCnt === 0" text="삭제" @click="onClickDeleteJobNotice(jobNotice.jobNoticeSeq)" class="clickable cancel"/>
              </td>
            </tr>
        </table>
        <div class="result-none" v-if="list.length === 0">
            등록된 채용공고가 없습니다.
        </div>
    </div>
    <Paginate
        v-if="list.length > 0"
        v-model="currentPage"
        :page-count="totalPages"
        :click-handler="onSelectedPage"
    />
  </div>
  <div v-else>
    <MyPageJobPostingApplicantListView
      :jobNoticeSeq="selectedJobNoticeSeq"
      :fileYn="selectedFileYn"
      :corpId="selectedCorpId"
      :userId="selectedUserId"
      @go-back="isApplicantView = false"
    />
  </div>
  <JobFairReusmeCreatePolicyModal ref="jobFairReusmeCreatePolicyModal" />
  <JobFairJobNoticeDetailModal ref="jobNoticeModal" /> <!-- 채용공고 보기 -->
</template>

<style lang="scss" scoped>
@import "../../../../../../assets/scss/theme.scss";

/* Filter */
.filter-wrap {
    margin-bottom: 40px;
    &.open {
        p {
            &:before {
                transition-duration: 0.2s;
                -webkit-transform: translateY(-50%) rotate(225deg);
                transform: translateY(-50%) rotate(225deg);
            }
        }
        .filter {
            display: block;
            border-bottom: $border_line;
        }
    }
    p {
        position: relative;
        @include font-size_md;
        background: $gray-light_color;
        border: $border_line;
        font-weight: 700;
        cursor: pointer;
        padding: 10px 20px;
        &:before {
            content: "";
            position: absolute;
            top: 50%;
            right: 20px;
            display: block;
            width: 8px;
            height: 8px;
            border-top: 1px solid #000000;
            border-left: 1px solid #000000;
            -webkit-transform: translateY(-50%) rotate(45deg);
            transform: translateY(-50%) rotate(45deg);
            transition-duration: 0.2s;
        }
    }
    .filter {
        display: none;
        border-left: $border_line;
        border-right: $border_line;
        dl {
            display: flex;
            align-items: center;
            dt {
                display: flex;
                align-items: center;
                width: 180px;
                height: 48px;
                font-weight: 700;
                padding: 10px 10px 10px 20px;
                .select {
                    font-weight: initial;
                }
            }
            dd {
                width: calc(100% - 180px);
                padding: 10px 20px 10px 0;
                .grid {
                    span {
                        margin: 0 4px;
                    }
                }
            }
            &:first-of-type {
                padding-top: 10px;
            }
            &:last-of-type {
                padding-bottom: 10px;
            }
        }
        .btn-wrap {
            border-top: $border_line;
            padding: 10px 20px;
            margin: 0;
            button {
                margin: 0 5px;
            }
        }
        .text-box {
            margin-top: 0;
        }
    }
}

.clickable {
  cursor: pointer;
  &.cancel {
    color: #E71414;
    text-decoration: underline;
    &:hover {
      font-weight: 700;
    }
  }
}

.white-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: white;
}

.blueTextButton { color: $blue_color; }
</style>